import { Map } from 'immutable';
import React, { useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
    generatePath,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';

import {
    getChatSessionDetails,
    getSessionContent,
    getSessionContentHistory,
} from 'flow/actions/chat-actions';
import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { Attachment, File as ChatFile } from 'flow/types/Chats';
import { selectSessionImages } from 'flow/selectors/chat-message-selectors';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { routes } from 'flow/types/routes';
import { getAttachmentLink, getAttachmentPreviewURL } from 'flow/utils';
import IconButton from 'flow/components/ui-components/IconButton';
import { copyImage, ChatContent } from 'flow/utils/message-utils';
import { LocationType } from 'flow/types/Location';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useModal } from 'flow/contexts/ModalProvider';

const MessagesImagesPage = () => {
    const { i18next } = useAppContext();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { pathname } = useLocation();
    const { openModal } = useModal();

    const fileDetails = useAppSelector((state) =>
        state.chats.getIn(['details', id], Map()),
    );

    const storedImages = useAppSelector((state) =>
        selectSessionImages(state, id),
    );
    const images = useMemo(() => storedImages?.reverse(), [storedImages]);

    useEffect(() => {
        dispatch(getChatSessionDetails(id));
        dispatch(getSessionContent(id, 'images'));
    }, [dispatch, id]);

    if (!images) {
        return <Loading />;
    }

    const handleImageClick = (file: TypedMap<ChatFile>) =>
        openModal('imagePreview', {
            messageKey: file.get('key'),
            sessionKey: id,
        });

    return (
        <Page
            header={i18next.t('Chat.Details.Images')}
            onClickBack={() =>
                history.push(
                    history.location.state?.from
                        ? history.location.state?.from
                        : generatePath(routes['messages.id.settings'], { id }),
                    { from: pathname },
                )
            }
            mainId="main-scrollable-div"
        >
            <InfiniteScroll
                dataLength={images.size || 0}
                next={() => {
                    dispatch(
                        getSessionContentHistory(
                            id,
                            images.last()?.get('key'),
                            ChatContent.IMAGES,
                        ),
                    );
                }}
                hasMore={images.size < fileDetails?.get('numberOfImages')}
                loader={<></>}
                scrollableTarget="main-scrollable-div"
                // The page scrolls main-scrollable-div, so the library's own
                // overflow only clips the focus ring of the edge images and of
                // their menus.
                className="!overflow-visible"
            >
                <div className="grid grid-cols-4 gap-6 pe-2 pt-2 pb-3">
                    {images
                        .map((file) => {
                            const attachment = file.get('attachment');

                            if (!attachment) {
                                return null;
                            }

                            const src = attachment
                                .get('fileName')
                                .match(/\.gif$/i)
                                ? getAttachmentLink(id, attachment)
                                : getAttachmentPreviewURL(attachment);

                            return (
                                <div
                                    key={file.get('key')}
                                    className="group relative rounded-sm"
                                >
                                    {/* The picture is the control that
                                        opens it, so it has to be a button: a
                                        click handler on the img alone is
                                        unreachable by keyboard. The button
                                        takes its name from the alt. It clips
                                        the zoom on hover itself: a wrapper
                                        would clip its focus ring too. */}
                                    <button
                                        type="button"
                                        onClick={() => handleImageClick(file)}
                                        className="block w-full cursor-pointer overflow-hidden rounded-sm"
                                    >
                                        <img
                                            src={src}
                                            alt={attachment.get('fileName')}
                                            className="aspect-square w-full rounded-sm object-cover transition-all duration-200 group-hover:scale-110"
                                        />
                                    </button>

                                    <QuickAccessMenu
                                        id={id}
                                        attachment={attachment}
                                    />
                                </div>
                            );
                        })
                        .toArray()}

                    {images.size < fileDetails?.get('numberOfImages') ? (
                        <div className="animate-fade aspect-square w-full text-gray-200">
                            <Skeleton.Image />
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            </InfiniteScroll>
        </Page>
    );
};

interface QuickAccessMenuProps {
    id: ChatSessionKey;
    attachment: TypedMap<Attachment>;
}

const QuickAccessMenu = ({ id, attachment }: QuickAccessMenuProps) => {
    const { i18next } = useAppContext();

    return (
        <div className="shadow-base sr-only-until-hover-or-focus absolute -end-2 -top-2 flex items-center justify-center rounded-sm bg-white">
            <a
                aria-label={i18next.t('Tooltip.Audio.Download')}
                className="hover:darken-10 flex items-center justify-center rounded-sm border-none p-1"
                href={getAttachmentLink(id, attachment)}
                download={true}
            >
                <Icon iconKey="get_app" />
            </a>

            <IconButton
                aria-label={i18next.t('Tooltip.Title.Copy')}
                className="hover:darken-10 rounded-sm border-none p-1"
                onClick={() => copyImage(getAttachmentLink(id, attachment))}
            >
                <Icon iconKey="copy" />
            </IconButton>
        </div>
    );
};

export default MessagesImagesPage;
